import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Query } from "@tanstack/react-query";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import {
	Alert,
	ButtonStandard,
	FormCheckboxGroup,
	FormDatePicker,
	FormHandle,
	FormSingleSelect,
	FormSubscribe,
	ModalBase,
	PYForm,
	SubmitButton,
	toast,
} from "@performyard/py-components";
import { Text as Typography } from "@performyard/py-components";

import { Grid, GridCol } from "../../../../../../components/Grid";
import Spacer from "../../../../../../components/Spacer";

import { useSetters as useSurveyParticipantsSetters } from "../../pages/SurveyParticipants/store/store";
import { useSurveyEmailStatus } from "../../pages/SurveyResults/hooks/useSurveyEmailStatus";

import { router } from "../../../../../../app/router";

import { SURVEY_DETAILS_MODALS } from "../../store/model";
import { useModal, useSetters, useSurveyStatus } from "../../store/store";

import { useReScheduleSurveyEmail, useUpdateSurvey } from "../../../../../../api/hooks";
import { useCompanySettings } from "../../../../../../api/hooks/defs/company";
import useSearchQueryParams from "../../../../../../hooks/useSearchQueryParams";
import { searchParamsSchema } from "../../hooks/useCompareDraftToSaved";
import { useSurvey } from "../../hooks/useSurvey";

import { formatErrorMessage } from "../../../../../../utils/TanstackQueryErrorHandling";
import { DateNormalizationEffect, normalizeDateTime } from "./utils";

const surveyAccessOptions = [
	{ label: "All employees have access", value: "all-employees" as const },
	{ label: "Only selected employees have access", value: "selected-employees" as const },
];

const baseSurveySettingsFormSchema = z.object({
	scheduleDates: z.array(z.string()),
	startDate: z.date().nullable().optional(),
	endDate: z.date().nullable().optional(),
	surveyAccess: z.enum(["all-employees", "selected-employees"]),
});

type SurveySettingsFormValues = z.infer<typeof baseSurveySettingsFormSchema>;
type SurveySettingsFormSchema = z.ZodEffects<
	typeof baseSurveySettingsFormSchema,
	SurveySettingsFormValues,
	SurveySettingsFormValues
>;

// Schema factory that accepts surveyStatus and companyTimezone to conditionally validate start date
const createSurveySettingsFormSchema = (
	surveyStatus: string | null,
	companyTimezone: string
): SurveySettingsFormSchema =>
	baseSurveySettingsFormSchema.superRefine((data, ctx) => {
		const hasScheduledDates = data.scheduleDates.includes("schedule-dates");
		// Get current time in company timezone for proper comparison
		const now = utcToZonedTime(new Date(), companyTimezone);
		// Only validate start date for scheduled/draft surveys (not active ones)
		const shouldValidateStartDateFuture = ["scheduled", "draft"].includes(surveyStatus ?? "");

		if (hasScheduledDates) {
			// Start date is required when scheduling (for scheduled/draft only)
			if (!data.startDate && shouldValidateStartDateFuture) {
				ctx.addIssue({
					code: "custom",
					message: "Start date is required",
					path: ["startDate"],
				});
			} else if (data.startDate && data.startDate < now && shouldValidateStartDateFuture) {
				ctx.addIssue({
					code: "custom",
					message: "Start date must be in the future",
					path: ["startDate"],
				});
			}

			// End date validations (only if end date is provided)
			if (data.endDate) {
				if (data.endDate < now) {
					ctx.addIssue({
						code: "custom",
						message: "End date must be in the future",
						path: ["endDate"],
					});
				} else if (data.startDate && data.endDate <= data.startDate) {
					ctx.addIssue({
						code: "custom",
						message: "End date must be after start date",
						path: ["endDate"],
					});
				}
			}
		}
	});

export const SurveySettingsModal = ({ surveyId }: { surveyId: string }) => {
	const { survey } = useSurvey({ surveyId });
	const modal = useModal();
	const setters = useSetters();
	const surveyStatus = useSurveyStatus();
	const surveyParticipantsSetters = useSurveyParticipantsSetters();

	const formRef = useRef<FormHandle<SurveySettingsFormSchema>>(null);

	const [originalValues, setOriginalValues] = useState<SurveySettingsFormValues | null>(null);
	const { data: companySettings } = useCompanySettings();
	const companyTimezone = useMemo(() => {
		return companySettings?.default_timezone || "America/New_York";
	}, [companySettings?.default_timezone]);

	// Create schema dynamically based on survey status and company timezone
	const surveySettingsFormSchema = useMemo(
		() => createSurveySettingsFormSchema(surveyStatus, companyTimezone),
		[surveyStatus, companyTimezone]
	);

	const { queryParams: searchParams } = useSearchQueryParams({
		schema: searchParamsSchema,
	});
	const tab = searchParams.tab;

	const { autoflowId, scheduledInTheFuture } = useSurveyEmailStatus(surveyId);
	const { mutate: reScheduleSurvey } = useReScheduleSurveyEmail();

	// Compute initial values from survey data
	const initialValues = useMemo((): SurveySettingsFormValues => {
		const storedStartDate = survey?.startDate ? new Date(survey.startDate) : null;
		const storedEndDate = survey?.endDate ? new Date(survey.endDate) : null;

		// Convert to company timezone
		let initialStartDate = storedStartDate ? utcToZonedTime(storedStartDate, companyTimezone) : null;
		let initialEndDate = storedEndDate ? utcToZonedTime(storedEndDate, companyTimezone) : null;

		// Normalize times to valid options (snap to closest valid hour)
		if (initialStartDate) {
			initialStartDate = normalizeDateTime(initialStartDate, false);
		}
		if (initialEndDate) {
			// isEndDate=true for special 11:59 PM → next day midnight handling
			initialEndDate = normalizeDateTime(initialEndDate, true);
		}

		const initialScheduleDates = survey?.scheduleDates ?? !!survey?.startDate;
		const initialSurveyAccess = survey?.participantSelection?.enabled ? "selected-employees" : "all-employees";

		return {
			scheduleDates: initialScheduleDates ? ["schedule-dates"] : [],
			startDate: initialStartDate,
			endDate: initialEndDate,
			surveyAccess: initialSurveyAccess,
		};
	}, [
		survey?.startDate,
		survey?.endDate,
		survey?.scheduleDates,
		survey?.participantSelection?.enabled,
		companyTimezone,
	]);

	// Set original values when modal opens
	useEffect(() => {
		if (modal === SURVEY_DETAILS_MODALS.SURVEY_SETTINGS) {
			setOriginalValues(initialValues);
			surveyParticipantsSetters.setSurveyAccess(initialValues.surveyAccess);
		}
	}, [modal, initialValues]);

	const handleSurveyAccessChange = (newAccess: "all-employees" | "selected-employees") => {
		if (newAccess === "selected-employees") {
			surveyParticipantsSetters.setEditParticipants(true);
			if (tab !== "participants") {
				router.navigate(`/surveys/${surveyId}?tab=participants`);
			}
			setters.setModal(null);
			surveyParticipantsSetters.setSurveyAccess("selected-employees");
		} else {
			setters.setModal(SURVEY_DETAILS_MODALS.SELECT_ALL_EMPLOYEES_MODAL);
		}
	};

	const handleClose = () => {
		setters.setModal(null);
		setOriginalValues(null);
	};

	const onSuccess = (formValues: SurveySettingsFormValues) => {
		// if the email has been scheduled and the start date has changed, reschedule the email
		const startDateChanged =
			formValues.startDate &&
			originalValues?.startDate &&
			formValues.startDate.toISOString() !== originalValues.startDate.toISOString();

		if (startDateChanged && autoflowId && scheduledInTheFuture) {
			reScheduleSurvey({ surveyId });
		}

		// Check if survey access changed
		if (formValues.surveyAccess !== originalValues?.surveyAccess) {
			handleSurveyAccessChange(formValues.surveyAccess);
		}

		handleClose();
		toast.success("Your changes have been saved");
	};

	const onError = (
		error: { data?: { message?: string } },
		mutation: Query<unknown, Error, unknown, readonly unknown[]>
	) => {
		const messages = formatErrorMessage(error, mutation);
		messages.forEach((message) => {
			toast.error(message);
		});
		handleClose();
	};

	const { mutate: updateSurvey } = useUpdateSurvey(surveyId, {
		onSuccess: () => {
			const formValues = formRef.current?.getFormValues();
			if (formValues) {
				onSuccess(formValues);
			}
		},
		onError,
	});

	const handleSubmit = ({ value }: { value: SurveySettingsFormValues }) => {
		const hasScheduledDates = value.scheduleDates.includes("schedule-dates");
		const originalHasScheduledDates = originalValues?.scheduleDates.includes("schedule-dates");

		// Check what changed
		const startDateChanged = value.startDate?.toISOString() !== originalValues?.startDate?.toISOString();
		const endDateChanged = value.endDate?.toISOString() !== originalValues?.endDate?.toISOString();
		const scheduleChanged = hasScheduledDates !== originalHasScheduledDates || startDateChanged || endDateChanged;

		const accessChanged = value.surveyAccess !== originalValues?.surveyAccess;

		// If only access changed, handle it directly
		if (!scheduleChanged && accessChanged) {
			handleSurveyAccessChange(value.surveyAccess);
			return;
		}

		// Build update payload - only include fields that changed (matching original behavior)
		const publishUpdates: { startDate?: Date; endDate?: Date | null; scheduleDates?: boolean } = {};

		if (!hasScheduledDates) {
			publishUpdates.endDate = null;
			publishUpdates.scheduleDates = false;
			if (originalValues?.startDate && originalValues.startDate > new Date()) {
				publishUpdates.startDate = new Date();
			}
		} else {
			publishUpdates.scheduleDates = true;

			// Only include startDate if it changed from the original survey value
			if (value.startDate && startDateChanged) {
				publishUpdates.startDate = zonedTimeToUtc(value.startDate, companyTimezone);
			}

			// Only include endDate if it changed from the original survey value
			if (endDateChanged) {
				publishUpdates.endDate = value.endDate ? zonedTimeToUtc(value.endDate, companyTimezone) : null;
			}
		}

		updateSurvey(publishUpdates);
	};

	const handleDiscard = () => {
		if (originalValues) {
			formRef.current?.reset();
		}
	};

	const startDateDisabled = surveyStatus === "active";
	const isScheduleDisabled = survey?.scheduleDates ?? !!survey?.startDate;

	const [animateRef] = useAutoAnimate();

	return (
		<ModalBase
			isOpen={modal === SURVEY_DETAILS_MODALS.SURVEY_SETTINGS}
			onClose={handleClose}
			dangerouslySetClassName="w-[800px]"
		>
			<div className="px-4 pb-4">
				<div className="mb-8 text-center">
					<Typography size="sub-heading" weight="semi-bold">
						Survey Settings
					</Typography>
					<Typography size="paragraph-2" weight="regular">
						{survey?.name}
					</Typography>
				</div>

				<PYForm
					ref={formRef}
					schema={surveySettingsFormSchema}
					defaultValues={initialValues}
					onSubmit={handleSubmit}
					id="survey-settings-form"
				>
					{/* Normalize dates when user types invalid times */}
					<FormSubscribe
						id="date-normalization"
						selector={(state: { values: SurveySettingsFormValues }) => ({
							startDate: state.values.startDate,
							endDate: state.values.endDate,
						})}
					>
						{({ startDate, endDate }) => (
							<DateNormalizationEffect
								startDate={startDate}
								endDate={endDate}
								setFieldValue={(name, value) => {
									// setFieldValue exists on the form API but isn't exposed in FormHandle type
									const formApi = formRef.current as unknown as {
										setFieldValue: (name: string, value: unknown) => void;
									} | null;
									formApi?.setFieldValue(name, value);
								}}
							/>
						)}
					</FormSubscribe>

					<Grid cols={2}>
						<GridCol>
							<div>
								<Typography size="paragraph-2" color="neutral-9" weight="semi-bold">
									Survey Schedule
								</Typography>
								<Spacer height={1} />
								<Typography size="paragraph-3" color="neutral-9" weight="regular">
									Schedule a survey to have more control over when participants can access it
								</Typography>
							</div>
						</GridCol>
						<GridCol>
							<div ref={animateRef} className="flex flex-col gap-2">
								<FormCheckboxGroup
									id="scheduleDates"
									options={[
										{
											label: "Schedule Start and End Date",
											value: "schedule-dates",
											disabled: isScheduleDisabled,
										},
									]}
								/>
								<FormSubscribe
									id="date-fields"
									selector={(state: { values: SurveySettingsFormValues }) => ({
										hasScheduledDates: state.values.scheduleDates.includes("schedule-dates"),
									})}
								>
									{({ hasScheduledDates }) =>
										hasScheduledDates ? (
											<div className="flex flex-col gap-4">
												<FormDatePicker
													id="startDate"
													label="Start Date"
													disabled={startDateDisabled || surveyStatus === "closed"}
													showTimeDropdown={true}
												/>
												<FormDatePicker
													id="endDate"
													label="End Date"
													disabled={surveyStatus === "closed"}
													showTimeDropdown={true}
												/>
												<Alert
													message={`Company timezone is set to ${companyTimezone}`}
													type="neutral"
												/>
											</div>
										) : null
									}
								</FormSubscribe>
							</div>
						</GridCol>
					</Grid>

					<Spacer height={2} />
					<div className="w-full border-b-[1px] border-solid border-neutral-2"></div>
					<Spacer height={2} />

					<Grid cols={2}>
						<GridCol>
							<div>
								<Typography size="paragraph-2" color="neutral-9" weight="semi-bold">
									Survey Access
								</Typography>
								<Spacer height={1} />
								<Typography size="paragraph-3" color="neutral-9" weight="regular">
									Adjust the access to control who can participate in the survey
								</Typography>
							</div>
						</GridCol>
						<GridCol>
							<FormSingleSelect
								id="surveyAccess"
								label="Survey Access"
								options={surveyAccessOptions}
								disabled={surveyStatus === "closed"}
							/>
						</GridCol>
					</Grid>

					<Spacer height={4} />

					<FormSubscribe
						id="form-buttons"
						selector={(state: { values: SurveySettingsFormValues }) => ({
							values: state.values,
						})}
					>
						{({ values }) => {
							const hasScheduledDates = values.scheduleDates.includes("schedule-dates");
							const originalHasScheduledDates = originalValues?.scheduleDates.includes("schedule-dates");

							const scheduleChanged =
								hasScheduledDates !== originalHasScheduledDates ||
								values.startDate?.toISOString() !== originalValues?.startDate?.toISOString() ||
								values.endDate?.toISOString() !== originalValues?.endDate?.toISOString();

							const accessChanged = values.surveyAccess !== originalValues?.surveyAccess;
							const hasChanges = scheduleChanged || accessChanged;

							return (
								<div className="flex justify-end gap-2">
									{hasChanges && (
										<div>
											<ButtonStandard
												size="large"
												type="button"
												color="neutral"
												variant="outline"
												onClick={handleDiscard}
											>
												Discard
											</ButtonStandard>
										</div>
									)}
									<div>
										<SubmitButton
											size="large"
											color="green"
											disabled={surveyStatus === "closed" || !hasChanges}
										>
											Save Changes
										</SubmitButton>
									</div>
								</div>
							);
						}}
					</FormSubscribe>
				</PYForm>
			</div>
		</ModalBase>
	);
};
